/**
 * useShellAuras - what everyone radiates, and the actor as the map finds them.
 *
 * AURAS (v8.368.8) are gathered per reader by token distance and team -
 * memoised on the tokens and the roster's projections, which is exactly what
 * can move them. One map serves the actor's context (the roll, the ledger,
 * both forecasts) and the turn tracker's initiative, so they cannot disagree.
 *
 * `actorProjection` is the acting combatant AS THE MAP FINDS THEM: `tabs`
 * already folds the ground into every projection, and the actor stands on
 * the battlefield like anybody else - reading their own entry is what stops
 * the attacker being the one person the mud does not touch. NOT cover: that
 * is a relation to whoever is shooting, and here the actor is the shooter.
 *
 * Returns `{ initiativeModFor(combatantId), actorProjection, actorTarget }`
 * (undefined / null with nobody acting). Extracted from DeathWatchSimulator.
 */
import { useMemo, useCallback } from 'react';
import { aurasByProximity, auraInitiative } from '../engine/exposureAuras';
import { projectDefender } from '../engine/roster/projection';

/** The actor's projection from the live tabs, with the auras reaching them. */
export function actorProjectionOf(actor, tabs, aurasByReader) {
  if (!actor) return undefined;
  const projected = tabs.find((t) => t.id === actor.id)?.target ?? projectDefender(actor);
  const auras = aurasByReader.get(actor.id);
  return auras ? { ...projected, auras } : projected;
}

/** @param {object} args - rosterTabs (stored), tabs (live), tokens, actor. */
export default function useShellAuras({ rosterTabs, tabs, tokens, actor }) {
  const aurasByReader = useMemo(
    () =>
      aurasByProximity(
        rosterTabs.map((t) => ({ id: t.id, bearer: t.target })),
        tokens,
      ),
    [rosterTabs, tokens],
  );
  const initiativeModFor = useCallback(
    (combatantId) => auraInitiative(aurasByReader.get(combatantId)),
    [aurasByReader],
  );
  const actorProjection = useMemo(() => actorProjectionOf(actor, tabs, aurasByReader), [tabs, actor, aurasByReader]);
  // `actorTarget`: the same projection, as the actor's own sheet reads it (null for nobody).
  return { initiativeModFor, actorProjection, actorTarget: actorProjection ?? null };
}
